import { Stack, Typography, useTheme } from '@mui/material';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import StyledWhiteBox from 'src/components/common/StyledWhiteBox';
import RouterLinkNoUnderline from 'src/components/common/RouterLinkNoUnderline';
import CustomTable from 'src/components/Table';
import { RankByDataPackage } from 'src/features/AnalyticsPackage/Common/config';
import { useQueryParams } from 'src/hooks/useQueryParam';
import { useQueryListDataPackage } from 'src/services/queries/useQueryTradeData';
import { FORMAT_DATE_TIME } from 'src/utils/constants/config';
import { TypeDataPackage } from 'src/utils/constants/enum';
import { AuthRouteKeys } from 'src/utils/constants/routes';
import { getArrayDefault, swapElements } from 'src/utils/helpers/arrayHelpers';
import { handleQueryParamFromObject } from 'src/utils/helpers/queryParam';
import { renderNumberOrDefault } from 'src/utils/helpers/stringHelpers';
import { renderTextOrDefault } from 'src/utils/helpers/stringHelpers';
import { ITypeProductImportedDataPackage } from 'src/utils/typings/response';

const ProductImportPackage = () => {
  const theme = useTheme();
  const { selectColumns: selectColumnParam } = useQueryParams();
  const [params, setParams] = useState({
    page: 0,
    page_size: 25,
  });
  const {
    rank_by = RankByDataPackage.NumberBol,
    type_package = TypeDataPackage.top_20_export_company_in_country,
    trade_data_from = '',
    trade_data_to = '',
    country = '',
    is_worldwide,
  } = useQueryParams();
  const [selectedColumn, setSelectedColumn] = useState<string[]>([]);
  const isWorldwide = useMemo(() => is_worldwide === 'true', [is_worldwide]);

  const { data, isFetching } = useQueryListDataPackage({
    type_package: String(type_package) as TypeDataPackage,
    trade_data_from: String(trade_data_from),
    trade_data_to: String(trade_data_to),
    country: String(country),
    rank_by: String(rank_by) as RankByDataPackage,
    is_worldwide: isWorldwide,
  });

  const listProducts = getArrayDefault(data?.data);

  const listProductsPaginate = useMemo(() => {
    return listProducts.slice(params.page * params.page_size, (params.page + 1) * params.page_size);
  }, [params, listProducts]);

  const configColumn: ConfigCell<ITypeProductImportedDataPackage>[] = [
    {
      disablePadding: false,
      id: 'hs_code',
      label: 'HS Code',
      width: 300,
      cell: (data: ITypeProductImportedDataPackage) => {
        const searchString = handleQueryParamFromObject({
          hs_code: data.hs_code,
          start_date: dayjs(String(trade_data_from), FORMAT_DATE_TIME.MM_YYYY).format(FORMAT_DATE_TIME.yyyy_mm_dd),
          end_date: dayjs(String(trade_data_to), FORMAT_DATE_TIME.MM_YYYY).format(FORMAT_DATE_TIME.yyyy_mm_dd),
          imp_country_code: !isWorldwide ? country : '',
          _search_by: 'hs_code',
        });
        return (
          <RouterLinkNoUnderline target="_blank" to={AuthRouteKeys.TradeData + `?${searchString}`}>
            <Typography component={'span'} variant="body2" color="secondary.main">
              {renderTextOrDefault(data.hs_code)} - {renderTextOrDefault(data.hscode_description)}
            </Typography>
          </RouterLinkNoUnderline>
        );
      },
    },
    {
      disablePadding: false,
      id: 'foreign_exporters',
      label: 'Foreign Exporters',
      cell: (data: ITypeProductImportedDataPackage) => (
        <Typography component={'span'} noWrap variant="body2" color="text.secondary">
          {renderNumberOrDefault(data.foreign_exporters)}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'importers_in_country',
      label: 'Importers in Country',
      cell: (data: ITypeProductImportedDataPackage) => (
        <Typography component={'span'} noWrap variant="body2" color="text.secondary">
          {renderNumberOrDefault(data.importers_in_country)}
        </Typography>
      ),
    },
  ];

  const configColumnRankBy: ConfigCell<ITypeProductImportedDataPackage>[] = [
    {
      disablePadding: false,
      id: 'bol',
      label: 'No of B/L',
      cell: (data: ITypeProductImportedDataPackage) => (
        <Typography component={'span'} noWrap variant="body2" color="text.secondary">
          {renderNumberOrDefault(data.bol)}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'weight',
      label: 'Weight',
      cell: (data: ITypeProductImportedDataPackage) => (
        <Typography component={'span'} noWrap variant="body2" color="text.secondary">
          {renderNumberOrDefault(data.weight)}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'quantity',
      label: 'Quantity',
      cell: (data: ITypeProductImportedDataPackage) => (
        <Typography component={'span'} noWrap variant="body2" color="text.secondary">
          {renderNumberOrDefault(data.quantity)}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'value_usd',
      label: 'Value (USD)',
      cell: (data: ITypeProductImportedDataPackage) => (
        <Typography component={'span'} noWrap variant="body2" color="text.secondary">
          {renderNumberOrDefault(data.value_usd)}
        </Typography>
      ),
    },
  ];

  const sortedConfigRankBy = useMemo(() => {
    const indexRankBy = configColumnRankBy.findIndex((config) => config.id === rank_by);
    return swapElements(configColumnRankBy, 0, indexRankBy).filter((config) => selectedColumn.includes(config.id));
  }, [rank_by, selectedColumn]);

  useEffect(() => {
    /** Get Init value of selectedColumns state */
    const selectColumns = selectColumnParam
      ? String(selectColumnParam).split(',')
      : configColumnRankBy.map((item) => item.id);
    setSelectedColumn(selectColumns);
  }, []);

  return (
    <StyledWhiteBox p={theme.spacing(3)}>
      <Stack spacing={3} justifyContent={'space-between'} alignItems={'center'} direction={'row'} mb={3}>
        <Typography variant="h5">HS Code List</Typography>
      </Stack>
      <CustomTable
        page={params.page}
        pageSize={params.page_size}
        onChangePage={(e) => setParams({ ...params, ...{ page: e } })}
        onChangePageSize={(e) => setParams({ ...params, ...{ page: 0, page_size: e } })}
        totalRecords={listProducts.length}
        loading={isFetching}
        data={listProductsPaginate}
        dataKey="hs_code"
        configColumn={[...configColumn, ...sortedConfigRankBy]}
      />
    </StyledWhiteBox>
  );
};

export default ProductImportPackage;
